<script setup lang="ts">
const props = defineProps<{ embedded?: boolean; glass?: boolean }>();

const { locale, setLocale, t, load } = useI18n();

onMounted(load);

function pick(code: 'id' | 'en') {
  if (locale.value === code) return;
  setLocale(code);
}
</script>

<template>
  <div
    class="lang-switch"
    :class="{
      'lang-switch--embedded': props.embedded,
      'lang-switch--glass': props.glass,
    }"
    role="group"
    :aria-label="t('lang.idFull') + ' / ' + t('lang.enFull')"
  >
    <button
      type="button"
      class="lang-btn lang-btn--id"
      :class="{ 'lang-btn--active': locale === 'id' }"
      :title="t('lang.idFull')"
      :aria-pressed="locale === 'id'"
      @click="pick('id')"
    >
      <span class="lang-flag lang-flag--id" aria-hidden="true">
        <span class="lang-flag__shine" />
      </span>
      <span class="lang-code">ID</span>
    </button>
    <button
      type="button"
      class="lang-btn lang-btn--en"
      :class="{ 'lang-btn--active': locale === 'en' }"
      :title="t('lang.enFull')"
      :aria-pressed="locale === 'en'"
      @click="pick('en')"
    >
      <span class="lang-flag lang-flag--en" aria-hidden="true">
        <span class="lang-flag__shine" />
      </span>
      <span class="lang-code">EN</span>
    </button>
  </div>
</template>

<style scoped>
.lang-switch {
  --ls-bg: #f1f5f9;
  --ls-line: #e2e8f0;
  --ls-muted: #334155;
  --ls-ink: #0f172a;
  --ls-active-bg: #fff;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px;
  border-radius: 999px;
  background: var(--ls-bg);
  border: 1px solid var(--ls-line);
  margin-right: 6px;
}

.lang-switch--embedded {
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  margin-right: 0;
  border-radius: 12px;
  padding: 4px;
}

.lang-switch--glass {
  --ls-bg: rgba(255, 255, 255, 0.08);
  --ls-line: rgba(255, 255, 255, 0.16);
  --ls-muted: rgba(226, 242, 240, 0.72);
  --ls-ink: #f8fffe;
  --ls-active-bg: rgba(255, 255, 255, 0.16);
  margin-right: 0;
  backdrop-filter: blur(14px) saturate(1.2);
  box-shadow: 0 10px 28px -18px rgba(0, 0, 0, 0.55);
}

:global(html.jgi-dark .lang-switch:not(.lang-switch--glass)) {
  --ls-bg: rgba(30, 41, 59, 0.9);
  --ls-line: rgba(148, 163, 184, 0.22);
  --ls-muted: #94a3b8;
  --ls-ink: #f8fafc;
  --ls-active-bg: #0f172a;
}

.lang-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border: none;
  cursor: pointer;
  padding: 6px 11px;
  border-radius: 999px;
  background: transparent;
  color: var(--ls-muted);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.04em;
  overflow: hidden;
  transition:
    background 0.2s ease,
    color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease;
}

.lang-switch--embedded .lang-btn {
  border-radius: 9px;
  padding: 8px 6px;
}

:global(html.jgi-device-hp .lang-switch--embedded),
:global(.jgi-device-studio--hp .lang-switch--embedded) {
  padding: 4px;
  border-radius: 12px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  width: 100%;
  box-sizing: border-box;
}

:global(html.jgi-device-hp .lang-switch--embedded .lang-btn),
:global(.jgi-device-studio--hp .lang-switch--embedded .lang-btn) {
  padding: 6px 4px;
  font-size: 11px;
  gap: 5px;
  border-radius: 8px;
  min-height: 34px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  justify-content: center;
}

:global(html.jgi-device-hp .lang-flag),
:global(.jgi-device-studio--hp .lang-flag) {
  width: 18px;
  height: 12px;
}

.lang-btn--active {
  background: var(--ls-active-bg);
  color: var(--ls-ink);
  box-shadow: 0 2px 8px -4px rgba(15, 23, 42, 0.35);
}

:global(html.jgi-dark .lang-btn--active) {
  box-shadow: 0 2px 10px -4px rgba(0, 0, 0, 0.55);
  border: 1px solid rgba(148, 163, 184, 0.2);
}

.lang-btn:hover {
  color: var(--ls-ink);
  transform: translateY(-1px);
}

.lang-btn:active {
  transform: translateY(0) scale(0.97);
}

/* ID — merah putih */
.lang-btn--id:hover,
.lang-btn--id:focus-visible {
  background: linear-gradient(135deg, rgba(227, 29, 28, 0.14), rgba(255, 255, 255, 0.55));
  box-shadow: 0 8px 18px -10px rgba(227, 29, 28, 0.55);
  color: #9f1239;
}

.lang-btn--id.lang-btn--active {
  background: linear-gradient(135deg, rgba(227, 29, 28, 0.12), #fff);
  color: #9f1239;
  box-shadow:
    0 4px 14px -6px rgba(227, 29, 28, 0.45),
    inset 0 0 0 1px rgba(227, 29, 28, 0.18);
}

/* EN — biru putih merah */
.lang-btn--en:hover,
.lang-btn--en:focus-visible {
  background: linear-gradient(135deg, rgba(1, 33, 105, 0.14), rgba(200, 16, 46, 0.1));
  box-shadow: 0 8px 18px -10px rgba(1, 33, 105, 0.55);
  color: #1e3a8a;
}

.lang-btn--en.lang-btn--active {
  background: linear-gradient(135deg, rgba(1, 33, 105, 0.12), #fff);
  color: #1e3a8a;
  box-shadow:
    0 4px 14px -6px rgba(1, 33, 105, 0.45),
    inset 0 0 0 1px rgba(1, 33, 105, 0.18);
}

.lang-switch--glass .lang-btn--id:hover,
.lang-switch--glass .lang-btn--id:focus-visible,
.lang-switch--glass .lang-btn--id.lang-btn--active {
  background: linear-gradient(135deg, rgba(227, 29, 28, 0.42), rgba(255, 255, 255, 0.14));
  color: #fff;
  box-shadow: 0 10px 22px -12px rgba(227, 29, 28, 0.75);
  border: 1px solid rgba(255, 180, 180, 0.28);
}

.lang-switch--glass .lang-btn--en:hover,
.lang-switch--glass .lang-btn--en:focus-visible,
.lang-switch--glass .lang-btn--en.lang-btn--active {
  background: linear-gradient(135deg, rgba(1, 33, 105, 0.5), rgba(200, 16, 46, 0.28));
  color: #fff;
  box-shadow: 0 10px 22px -12px rgba(56, 189, 248, 0.45);
  border: 1px solid rgba(147, 197, 253, 0.28);
}

.lang-btn:hover .lang-flag,
.lang-btn:focus-visible .lang-flag {
  transform: scale(1.12) rotate(-2deg);
  box-shadow:
    0 6px 14px -6px rgba(0, 0, 0, 0.35),
    inset 0 0 0 0.5px rgba(255, 255, 255, 0.45);
}

.lang-btn--active .lang-flag {
  transform: scale(1.06);
}

.lang-btn:hover .lang-flag__shine,
.lang-btn:focus-visible .lang-flag__shine {
  animation: lang-flag-shine 0.7s ease both;
}

.lang-flag {
  position: relative;
  width: 20px;
  height: 13px;
  border-radius: 3px;
  border: 1px solid rgba(15, 23, 42, 0.14);
  flex-shrink: 0;
  overflow: hidden;
  box-shadow:
    inset 0 0 0 0.5px rgba(255, 255, 255, 0.35),
    0 2px 6px -3px rgba(0, 0, 0, 0.25);
  transition:
    transform 0.22s cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 0.22s ease;
}

.lang-flag__shine {
  position: absolute;
  inset: 0;
  left: -60%;
  width: 40%;
  background: linear-gradient(
    105deg,
    transparent 0%,
    rgba(255, 255, 255, 0.55) 50%,
    transparent 100%
  );
  transform: skewX(-18deg);
  opacity: 0;
  pointer-events: none;
  z-index: 2;
}

.lang-flag--id {
  background: linear-gradient(180deg, #e31d1c 0 50%, #ffffff 50% 100%);
}

.lang-flag--en {
  background: linear-gradient(180deg, #012169 0 33%, #ffffff 33% 66%, #c8102e 66% 100%);
}

.lang-flag--en::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    transparent 0 38%,
    #ffffff 38% 42%,
    #c8102e 42% 58%,
    #ffffff 58% 62%,
    transparent 62% 100%
  );
  opacity: 0.95;
  z-index: 1;
}

.lang-code {
  line-height: 1;
  position: relative;
  z-index: 1;
}

@keyframes lang-flag-shine {
  from {
    left: -55%;
    opacity: 0;
  }
  35% {
    opacity: 1;
  }
  to {
    left: 120%;
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .lang-btn,
  .lang-flag,
  .lang-flag__shine {
    transition: none !important;
    animation: none !important;
    transform: none !important;
  }
}
</style>
